"use client";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { User, Mail, Lock, Eye, EyeOff, Upload, Save, RefreshCw, CheckCircle, X } from "lucide-react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify"; // Import toast for notifications

export default function AdminProfileTab() {
  const { user, token } = useAuth(); // Add token from AuthContext
  const [adminData, setAdminData] = useState({
    id: null,
    firstname: "",
    lastname: "",
    email: "",
    role: "",
    profileImage: null,
  });
  const [profileImage, setProfileImage] = useState(null);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showResetSuccess, setShowResetSuccess] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetError, setResetError] = useState("");
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    profileImageFile: null,
  });
  const [errors, setErrors] = useState({});

  // Fetch profile data using user.id from AuthContext
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user || !token || (user.role !== "Admin" && user.role !== "Super Admin")) {
        return; // Skip fetching if not authenticated or not Admin/Super Admin
      }
      try {
        const response = await axios.get(`https://backend.emerald.azadnoori.com/api/v1/users/${user.id}`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        const fetchedUser = response.data.data?.user;
        if (!fetchedUser) {
          throw new Error("User data not found in response");
        }
        setAdminData({
          id: fetchedUser.id || user.id,
          firstname: fetchedUser.firstname || "",
          lastname: fetchedUser.lastname || "",
          email: fetchedUser.email || "",
          role: fetchedUser.role || user.role,
          profileImage: fetchedUser.profileImage || null,
        });
        setFormData({
          firstname: fetchedUser.firstname || "",
          lastname: fetchedUser.lastname || "",
          email: fetchedUser.email || "",
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
          profileImageFile: null,
        });
        setProfileImage(fetchedUser.profileImage || null);
      } catch (error) {
        let errorMessage = "Failed to load profile. Please try again.";
        if (error.response?.status === 401) {
          errorMessage = "Session expired. Please log in again.";
          toast.error(errorMessage, {
            position: "top-right",
            icon: <X size={20} className="text-red-600" />,
          });
          return <Navigate to="/login" replace />;
        } else if (error.response?.status === 403) {
          errorMessage = "Not authorized to access this profile.";
        } else if (error.response?.status === 404) {
          errorMessage = "User profile not found. Please log out and log in again.";
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        setErrors({ general: errorMessage });
        toast.error(errorMessage, {
          position: "top-right",
          icon: <X size={20} className="text-red-600" />,
        });
      }
    };
    fetchProfile();
  }, [user, token]);

  // Redirect if not authenticated or not Admin/Super Admin
  if (!user || !token) {
    return <Navigate to="/login" replace />;
  }
  if (user.role !== "Admin" && user.role !== "Super Admin") {
    return <Navigate to="/unauthorized" replace />;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleImageUpload = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const imageUrl = URL.createObjectURL(file);
      setProfileImage(imageUrl);
      setFormData({ ...formData, profileImageFile: file });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstname.trim()) newErrors.firstname = "First name is required";
    if (!formData.lastname.trim()) newErrors.lastname = "Last name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (formData.currentPassword || formData.newPassword || formData.confirmPassword) {
      if (!formData.currentPassword) {
        newErrors.currentPassword = "Current password is required to change password";
      }
      if (formData.newPassword && formData.newPassword.length < 6) {
        newErrors.newPassword = "Password must be at least 6 characters";
      }
      if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsUpdating(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("firstname", formData.firstname);
      formDataToSend.append("lastname", formData.lastname);
      formDataToSend.append("email", formData.email);
      if (formData.currentPassword) formDataToSend.append("currentPassword", formData.currentPassword);
      if (formData.newPassword) formDataToSend.append("newPassword", formData.newPassword);
      if (formData.confirmPassword) formDataToSend.append("confirmPassword", formData.confirmPassword);
      if (formData.profileImageFile) formDataToSend.append("profileImage", formData.profileImageFile);
      const response = await axios.patch(`https://backend.emerald.azadnoori.com/api/v1/users/${user.id}`, formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`, // Add Authorization header
        },
        withCredentials: true,
      });
      const updatedUser = response.data.data?.user;
      if (!updatedUser) {
        throw new Error("Updated user data not found in response");
      }
      setAdminData({
        id: updatedUser.id || user.id,
        firstname: updatedUser.firstname || "",
        lastname: updatedUser.lastname || "",
        email: updatedUser.email || "",
        role: updatedUser.role || user.role,
        profileImage: updatedUser.profileImage || null,
      });
      setFormData({
        ...formData,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
        profileImageFile: null,
      });
      setProfileImage(updatedUser.profileImage || null);
      setShowSuccess(true);
      toast.success("Profile updated successfully!", {
        position: "top-right",
        icon: <CheckCircle size={20} className="text-emerald-600" />,
      });
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        const errorMessage = error.response?.data?.message || error.message || "Failed to update profile";
        setErrors({ general: errorMessage });
        toast.error(errorMessage, {
          position: "top-right",
          icon: <X size={20} className="text-red-600" />,
        });
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      setResetError("Email is required");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resetEmail)) {
      setResetError("Email is invalid");
      return;
    }
    try {
      const response = await axios.post(
        "https://backend.emerald.azadnoori.com/api/v1/users/password-reset",
        { email: resetEmail },
        {
          headers: { Authorization: `Bearer ${token}` }, // Add Authorization header
          withCredentials: true,
        }
      );
      setShowResetModal(false);
      setResetEmail("");
      setResetError("");
      setShowResetSuccess(true);
      toast.success("Password reset instructions sent to your email!", {
        position: "top-right",
        icon: <CheckCircle size={20} className="text-emerald-600" />,
      });
      setTimeout(() => setShowResetSuccess(false), 3000);
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to send reset instructions. Please try again later.";
      setResetError(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        icon: <X size={20} className="text-red-600" />,
      });
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">My Profile</h2>
      {showSuccess && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 bg-emerald-100 border border-emerald-200 text-emerald-800 rounded-lg p-4 flex items-center"
        >
          <CheckCircle className="mr-2 text-emerald-600" size={20} />
          <span>Your profile has been successfully updated!</span>
        </motion.div>
      )}
      {showResetSuccess && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 bg-emerald-100 border border-emerald-200 text-emerald-800 rounded-lg p-4 flex items-center"
        >
          <CheckCircle className="mr-2 text-emerald-600" size={20} />
          <span>Password reset instructions have been sent to your email!</span>
        </motion.div>
      )}
      {errors.general && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 bg-red-100 border border-red-200 text-red-800 rounded-lg p-4 flex items-center"
        >
          <X className="mr-2 text-red-600" size={20} />
          <span>{errors.general}</span>
        </motion.div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-1">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden p-6">
            <div className="flex flex-col items-center">
              <div className="w-32 h-32 bg-gray-100 rounded-full mb-4 overflow-hidden flex items-center justify-center">
                {profileImage ? (
                  <img
                    src={profileImage.startsWith("/uploads") ? `http://localhost:5000${profileImage}` : profileImage}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User size={64} className="text-gray-400" />
                )}
              </div>
              <h3 className="text-lg font-medium text-gray-800 mb-1">
                {adminData.firstname} {adminData.lastname}
              </h3>
              <p className="text-sm text-gray-500 mb-4">{adminData.email || "No email available"}</p>
              <span className="px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs font-medium mb-4">
                {adminData.role || "No role assigned"}
              </span>
              <div className="w-full">
                <label
                  htmlFor="profile-image"
                  className="flex items-center justify-center w-full px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer"
                >
                  <Upload size={16} className="mr-2" />
                  Change Photo
                </label>
                <input
                  type="file"
                  id="profile-image"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="md:col-span-2">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden p-6">
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                <div>
                  <label htmlFor="firstname" className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <div className="relative">
                    <input
                      id="firstname"
                      name="firstname"
                      type="text"
                      value={formData.firstname}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 ${
                        errors.firstname ? "border-red-500" : ""
                      }`}
                    />
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  </div>
                  {errors.firstname && <p className="mt-1 text-sm text-red-600">{errors.firstname}</p>}
                </div>
                <div>
                  <label htmlFor="lastname" className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <div className="relative">
                    <input
                      id="lastname"
                      name="lastname"
                      type="text"
                      value={formData.lastname}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 ${
                        errors.lastname ? "border-red-500" : ""
                      }`}
                    />
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  </div>
                  {errors.lastname && <p className="mt-1 text-sm text-red-600">{errors.lastname}</p>}
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <div className="relative">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 ${
                        errors.email ? "border-red-500" : ""
                      }`}
                    />
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  </div>
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                </div>
                <div className="border-t pt-6">
                  <h4 className="text-lg font-medium text-gray-800 mb-4">Change Password</h4>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                        Current Password
                      </label>
                      <div className="relative">
                        <input
                          id="currentPassword"
                          name="currentPassword"
                          type={showCurrentPassword ? "text" : "password"}
                          value={formData.currentPassword}
                          onChange={handleInputChange}
                          className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 ${
                            errors.currentPassword ? "border-red-500" : ""
                          }`}
                          placeholder="••••••••"
                        />
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        >
                          {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {errors.currentPassword && <p className="mt-1 text-sm text-red-600">{errors.currentPassword}</p>}
                    </div>
                    <div>
                      <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          id="newPassword"
                          name="newPassword"
                          type={showNewPassword ? "text" : "password"}
                          value={formData.newPassword}
                          onChange={handleInputChange}
                          className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 ${
                            errors.newPassword ? "border-red-500" : ""
                          }`}
                          placeholder="••••••••"
                        />
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {errors.newPassword && <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>}
                    </div>
                    <div>
                      <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                        Confirm New Password
                      </label>
                      <div className="relative">
                        <input
                          id="confirmPassword"
                          name="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 ${
                            errors.confirmPassword ? "border-red-500" : ""
                          }`}
                          placeholder="••••••••"
                        />
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
                    </div>
                  </div>
                </div>
                <div className="flex justify-between pt-4">
                  <button
                    type="button"
                    onClick={() => setShowResetModal(true)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center"
                  >
                    <RefreshCw size={16} className="mr-2" />
                    Forgot Password
                  </button>
                  <button
                    type="submit"
                    disabled={isUpdating}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center disabled:bg-emerald-400"
                  >
                    {isUpdating ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Number.POSITIVE_INFINITY, duration: 1, ease: "linear" }}
                        className="mr-2"
                      >
                        <RefreshCw size={16} />
                      </motion.div>
                    ) : (
                      <Save size={16} className="mr-2" />
                    )}
                    Save Changes
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Reset Password</h3>
              <button onClick={() => setShowResetModal(false)} className="p-1 rounded-full hover:bg-gray-100">
                <X size={20} />
              </button>
            </div>
            <p className="text-gray-600 mb-4">
              Enter your email address and we'll send you instructions to reset your password.
            </p>
            <form onSubmit={handleResetPassword}>
              <div className="mb-4">
                <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <div className="relative">
                  <input
                    id="reset-email"
                    type="email"
                    value={resetEmail}
                    onChange={(e) => {
                      setResetEmail(e.target.value);
                      setResetError("");
                    }}
                    className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 ${
                      resetError ? "border-red-500" : ""
                    }`}
                    placeholder="your@email.com"
                  />
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                </div>
                {resetError && <p className="mt-1 text-sm text-red-600">{resetError}</p>}
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowResetModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors mr-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center"
                >
                  <RefreshCw size={16} className="mr-2" />
                  Reset Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}